<template lang="pug">
.dashboard-wrapper
  b-row
    b-col
      b-card(no-body, class="pt-2 px-2")
        filters(
          :dateRangeFilterShow="true",
          :processTypeFilterShow="true",
          :processFilterShow="true",
          @changeFilter="updateFilter",
        )

  b-row(v-if="isLoading || isLoadingCases || isLoadingComplaints || isLoadingConf || isLoadingComm || isLoadingRisks || isLoadingRiskPrediction")
    b-col
      b-card(class="py-5")
        loading(
          :active="true"
          :is-full-page="false"
          color="#498ceb"
        )

  div(v-else)

    //- Risk Prediction Gauge
    b-row
      b-col(cols="12")
        apex-gauge-risk-prediction(
          :gaugeValue="riskPredictionValue",
          :trendDirection="riskTrendDirection",
          :trendPercentage="riskTrendPercentage"
        )

    b-row
      //- Global Compliance
      b-col(md="6")
        //- Complaints
        donut-chart-state(
          v-if="complaintStatesData.labels && complaintStatesData.labels.length"
          :data="complaintStatesData"
          i18nTitleVariable="complaintsByState"
          customHeight="339"
          @filterByState="filterByState"
        )

        //- Evaluations global compliance
        bar-chart-progress-fixed(:progress-data="progressCompliance", :cursorPointer="true", :includeSubtitle="true", @goToGlobalDashboard="goToGlobalDashboard")

      b-col(md="6")
        //- Cases
        donut-chart-state(
          v-if="caseStatesData.labels && caseStatesData.labels.length"
          :data="caseStatesData"
          i18nTitleVariable="casesByState"
          customHeight="339"
          @filterByState="filterByState"
        )

        //- Evaluations compliance by area
        bar-chart-compliance-fixed(:locationData="locationData", :emitToParentComponent="true", @filterByArea="filterByArea")

    //- Process Adherence and Commitments
    b-card(no-body)
      b-card-title(class="p-2 mb-0 text-center")
        | {{ $t('Adherence') }}

    b-row
      b-col(md="2")
        statistic-card-vertical-total(:statistic="confirmations_total", :isButton="true", customHeight="12.5rem", @goToDashboard="goToAdherenceDashboard")
      b-col(md="2")
        statistic-card-vertical-index(:statistic="adherence_index", i18nVariable="Initial Adherence", iconVariant="light-info", :isButton="true", customHeight="12.5rem", @goToDashboard="goToAdherenceDashboard")
      //- b-col(md="2")
        statistic-card-adherence(:statistic="adherenceTotal", :isButton="true", @goToDashboard="goToCommitmentDashboard")
      b-col(md="2")
        statistic-card-adjusted(:statistic="adherenceAdjusted", :isButton="true", @goToDashboard="goToCommitmentDashboard")
      b-col(md="2")
        statistic-card-commitments(:statistic="commitmentsGenerated", :isButton="true", @goToDashboard="goToCommitmentDashboard")
      b-col(md="2")
        statistic-card-compliance(:statistic="commitmentCompliance", :isButton="true", @goToDashboard="goToCommitmentDashboard")
      b-col(md="2")
        statistic-card-not-generated(:statistic="commitmentsNotGenerated", :isButton="true", @goToDashboard="goToCommitmentDashboard")

    b-row
      b-col(md="6")
        bar-chart-process(
          v-if="adherenceByProcess.labels && adherenceByProcess.labels.length",
          :process-data="adherenceByProcess",
          :emitToParentComponent="true",
          @filterByProcess="filterByProcess"
        )
      b-col(md="6")
        bar-chart-commitments(
          v-if="commitmentsData.labels && commitmentsData.labels.length",
          :commitments-data="commitmentsData",
          :emitToParentComponent="true",
          @filterByWorker="filterByWorker"
        )

    //- Inherent and Residual Risk Heat Maps
    b-card(no-body)
      b-card-title(class="p-2 mb-0 text-center")
        | {{ $t('label.heatMap') }}

    b-row
      b-col(md="6")
        bubble-chart-process(
          v-if="inherentRiskData && inherentRiskData.length",
          :risk-data="inherentRiskData"
          title-variable="inherentRiskMap"
        )
      b-col(md="6")
        bubble-chart-process(
          v-if="residualRiskData && residualRiskData.length",
          :risk-data="residualRiskData"
          title-variable="residualRiskMap"
        )
</template>

<script>
import { onMounted, ref, computed, watch } from '@vue/composition-api/dist/vue-composition-api'
import useNotifications from '@/composables/useNotifications'
import { BTable, BTableSimple, BThead, BTr, BTd, BCard, BCardHeader, BCardTitle, BPagination } from 'bootstrap-vue'
import JsonExcel from 'vue-json-excel'
import BarChartProgressFixed from '@/views/habit/confirmation/dashboard/charts/ChartjsHorizontalBarChartProgressFixed.vue'
import BarChartComplianceFixed from '@/views/habit/confirmation/dashboard/charts/ChartjsHorizontalBarChartComplianceFixed.vue'
import BarChartProcess from '@/views/habit/adherence/processes/charts/ChartjsBarChartProcess.vue'
import BarChartCommitments from '@/views/habit/adherence/commitment/charts/ChartjsHorizontalBarChartCommitments.vue'
import DonutChartState from '@/views/organization/case/list/charts/ApexDonutChartState.vue'
import ApexGaugeRiskPrediction from '@/views/habit/general/charts/ApexGaugeRiskPrediction.vue'
import StatisticCardVerticalTotal from '@/views/habit/adherence/processes/charts/StatisticCardVerticalTotal.vue'
import StatisticCardVerticalIndex from '@/views/habit/adherence/processes/charts/StatisticCardVerticalIndex.vue'
import StatisticCardAdherence from '@/views/habit/adherence/commitment/charts/StatisticCardVerticalAdherence.vue'
import StatisticCardAdjusted from '@/views/habit/adherence/commitment/charts/StatisticCardVerticalAdjusted.vue'
import StatisticCardCommitments from '@/views/habit/adherence/commitment/charts/StatisticCardVerticalCommitments.vue'
import StatisticCardCompliance from '@/views/habit/adherence/commitment/charts/StatisticCardVerticalCompliance.vue'
import StatisticCardNotGenerated from '@/views/habit/adherence/commitment/charts/StatisticCardVerticalNotGenerated.vue'
import BubbleChartProcess from '@/views/habit/adherence/processes/charts/ChartjsBubbleChartProcess.vue'
import Filters from '@/views/organization/Filters.vue'
import i18n from '@/libs/i18n'
import useCommonDashboards from '@/views/habit/useCommonDashboards'
import Loading from 'vue-loading-overlay'
import 'vue-loading-overlay/dist/vue-loading.css'
import store from '@/store'
import { permissionScope } from '@/composables/usePermission'
import useCommon from '@/views/organization/useCommon'
import { get } from 'idb-keyval'
import { complianceCalculationFixedNumber, probabilityValues, impactValues } from '@/constants'
import ViewButton from '@/views/components/Shared/Buttons/ViewButton.vue'
import UserButton from '@/views/components/Shared/Buttons/UserButton.vue'
import { useRouter } from '@core/utils/utils'
import { useLambda } from '@/views/habit/awsLambda'
import { oid, date } from '@/helpers/mongoHelpers'
import useMongoDBFunctions from '@/views/habit/useMongoDBFunctions'

export default {
  components: {
    BTable,
    BTableSimple,
    BThead,
    BTr,
    BTd,
    BCard,
    BCardHeader,
    BCardTitle,
    BPagination,
    JsonExcel,
    BarChartProgressFixed,
    BarChartComplianceFixed,
    BarChartProcess,
    BarChartCommitments,
    DonutChartState,
    StatisticCardVerticalTotal,
    StatisticCardVerticalIndex,
    StatisticCardAdherence,
    StatisticCardAdjusted,
    StatisticCardCommitments,
    StatisticCardCompliance,
    StatisticCardNotGenerated,
    BubbleChartProcess,
    Filters,
    Loading,
    ViewButton,
    UserButton,
    ApexGaugeRiskPrediction,
  },
  setup(props) {
    const userData = store.state?.userStore?.userData
    const userRole = userData.role
    const userId = userData.worker_id != null ? userData.worker_id.$oid : null
    const clientId = userData.role !== 'admin' ? userData.client.$oid : null
    const { default_language } = JSON.parse(localStorage.getItem('clientData') || '{}')
    const { username, email } = userData
    const { getItemsAggregate } = useLambda()
    const { casesQueryWithOffset, complaintsQueryWithOffset, commitmentReport } = useMongoDBFunctions()
    const userObjectId = oid(userId)
    const { router } = useRouter()
    const { getDatesFromRange, dateDiffInDays, processAdherence } = useCommonDashboards()
    const { handleError } = useCommon()
    const progressCompliance = ref({})
    const locationData = ref({})
    const locationFilter = ref([])
    const roleFilter = ref([])
    // Leer processFilter desde el store global
    const processFilter = computed(() => store.state.filtersStore.processFilter)
    const companyFilter = computed(() => store.state.filtersStore.companyFilter)
    const idFilter = computed(() => store.state.filtersStore.idFilter)
    const workerFilter = ref([])
    const supervisorFilter = ref([])
    const now = new Date()
    const currentMonth = now.getMonth()
    const currentYear = now.getFullYear()
    const currentDay = now.getDate()
    const lastDayOfMonthDate = new Date(currentYear, currentMonth + 1, 0)
    const lastDayOfMonthNumber = lastDayOfMonthDate.getDate()
    const thirtyDaysBeforeDate = new Date(currentYear, currentMonth, currentDay - 30)
    const dateRangeFilter = default_language === 'en'
      ? ref(`${thirtyDaysBeforeDate.getMonth() + 1}-${thirtyDaysBeforeDate.getDate()}-${thirtyDaysBeforeDate.getFullYear()} to ${currentMonth + 1}-${currentDay}-${currentYear}`)
      : ref(`${thirtyDaysBeforeDate.getDate()}-${thirtyDaysBeforeDate.getMonth() + 1}-${thirtyDaysBeforeDate.getFullYear()} to ${currentDay}-${currentMonth + 1}-${currentYear}`)
    const processTypeFilter = ref([])
    const isLoading = ref(true)
    const perPage = ref(50)
    const currentPage = ref(1)

    const caseStatesData = ref({})
    const complaintStatesData = ref({})
    const isLoadingCases = ref(true)
    const isLoadingComplaints = ref(true)

    const confirmations_total = ref(0)
    const adherence_index = ref(0)
    const adherenceByProcess = ref({})
    const isLoadingConf = ref(true)

    const adherenceTotal = ref(0)
    const adherenceAdjusted = ref(0)
    const commitmentsGenerated = ref(0)
    const commitmentCompliance = ref(0)
    const commitmentsNotGenerated = ref(0)
    const commitmentsData = ref({})
    const isLoadingComm = ref(true)

    const residualRiskData = ref([])
    const inherentRiskData = ref([])
    const quadrantCounters = ref({})
    const isLoadingRisks = ref(true)

    const riskPredictionValue = ref(0)
    const riskTrendDirection = ref(null)
    const riskTrendPercentage = ref(0)
    const isLoadingRiskPrediction = ref(true)

    // Global Compliance

    const listEvents = async () => {
      isLoading.value = true
      const [startFilter, endFilter] = getDatesFromRange(dateRangeFilter.value)

      try {
        const initialQuery = {
          client_id: oid(clientId),
          deleted: { $ne: true },
          'extendedProps.calendar': { $in: ['Pendientes', 'Realizadas', 'Vencidas', 'Futuras'] },
          start: { $gte: date(startFilter), $lt: date(endFilter) },
          isActionPlan: { $ne: true },
        }

        if (processFilter.value.length > 0) {
          initialQuery.process = { $in: processFilter.value.map(e => oid(e)) }
        }

        const finalQuery = {
          $and: [
            {
              $or: [
                { 'attendee.deleted': { $ne: true } },
                { 'attendee.deleted': true, 'extendedProps.calendar': 'Realizadas' },
                { attendee: null },
              ],
            },
          ],
        }

        if (roleFilter.value.length > 0) {
          finalQuery['attendee.roles'] = { $in: roleFilter.value.map(e => oid(e)) }
        }

        if (supervisorFilter.value.length > 0) {
          finalQuery['organizer._id'] = { $in: supervisorFilter.value }
        }

        if (locationFilter.value.length > 0) {
          finalQuery.$and[0].$or[0]['attendee.locations._id'] = { $in: locationFilter.value }
          finalQuery.$and[0].$or[1]['attendee.locations._id'] = { $in: locationFilter.value }
          finalQuery.$and[0].$or[2]['organizer.locations._id'] = { $in: locationFilter.value }
        }

        // Process type filter
        if (processTypeFilter.value.length) {
          finalQuery.$and.push({
            $or: processTypeFilter.value.map(codePrefix => ({
              'process.code': { $regex: `^${codePrefix}` },
            })),
          })
        }

        // Privileges filter
        if (permissionScope('confirmations-global', 'view') === 'own') {
          finalQuery.$and.push({
            $or: [
              { 'attendee._id': userId },
              { 'process.responsibles': userObjectId },
              { 'process.leadResponsibles': userObjectId },
              { 'process.evaluator': userObjectId },
            ],
          })
        }

        if (companyFilter.value.length > 0) {
          finalQuery['process.company'] = { $in: companyFilter.value.map(e => oid(e)) }
        }
  
        if (idFilter.value.length > 0) {
          finalQuery['process.id'] = { $in: idFilter.value.map(e => oid(e)) }
        }

        const pipeline = [
          { $match: initialQuery },
          {
            $lookup: {
              from: 'worker',
              localField: 'attendee',
              foreignField: '_id',
              as: 'attendee',
              pipeline: [{
                $project: {
                  deleted: 1, supervisors: 1, name: 1, locations: 1, roles: 1,
                },
              }, { $addFields: { _id: { $toString: '$_id' } } }, {
                $lookup: {
                  from: 'worker',
                  localField: 'supervisors',
                  foreignField: '_id',
                  as: 'supervisors',
                  pipeline: [{ $project: { name: 1, locations: 1 } }, { $addFields: { _id: { $toString: '$_id' } } }, {
                    $lookup: {
                      from: 'location', localField: 'locations', foreignField: '_id', as: 'locations', pipeline: [{ $project: { location: 1 } }, { $addFields: { _id: { $toString: '$_id' } } }],
                    },
                  }],
                },
              }, {
                $lookup: {
                  from: 'location', localField: 'locations', foreignField: '_id', as: 'locations', pipeline: [{ $project: { location: 1, zone: 1, agency: 1 } }, { $addFields: { _id: { $toString: '$_id' } } }],
                },
              }],
            },
          },
          {
            $lookup: {
              from: 'worker',
              localField: 'organizer',
              foreignField: '_id',
              as: 'organizer',
              pipeline: [{ $project: { name: 1, locations: 1 } }, { $addFields: { _id: { $toString: '$_id' } } }, {
                $lookup: {
                  from: 'location', localField: 'locations', foreignField: '_id', as: 'locations', pipeline: [{ $project: { location: 1, zone: 1, agency: 1 } }, { $addFields: { _id: { $toString: '$_id' } } }],
                },
              }],
            },
          },
          {
            $lookup: {
              from: 'process',
              localField: 'process',
              foreignField: '_id',
              as: 'process',
              pipeline: [{
                $project: {
                  name: 1, code: 1, responsibles: 1, leadResponsibles: 1, evaluator: 1, company: 1, id: 1,
                },
              }, { $addFields: { _id: { $toString: '$_id' } } }],
            },
          },
          { $addFields: { attendee: { $arrayElemAt: ['$attendee', 0] }, organizer: { $arrayElemAt: ['$organizer', 0] }, process: { $arrayElemAt: ['$process', 0] } } },
          { $match: finalQuery },
          {
            $lookup: {
              from: 'confirmation',
              localField: 'confirmation',
              foreignField: '_id',
              as: 'confirmation',
              pipeline: [{ $addFields: { _id: { $toString: '$_id' } } }, {
                $lookup: {
                  from: 'worker',
                  localField: 'supervisor',
                  foreignField: '_id',
                  as: 'supervisor',
                  pipeline: [{ $project: { name: 1, locations: 1 } }, { $addFields: { _id: { $toString: '$_id' } } }, {
                    $lookup: {
                      from: 'location', localField: 'locations', foreignField: '_id', as: 'locations', pipeline: [{ $project: { location: 1 } }, { $addFields: { _id: { $toString: '$_id' } } }],
                    },
                  }],
                },
              }, { $addFields: { supervisor: { $arrayElemAt: ['$supervisor', 0] } } }],
            },
          },
          {
            $lookup: {
              from: 'worker', localField: 'deletedBy', foreignField: '_id', as: 'deletedBy', pipeline: [{ $project: { name: 1 } }, { $addFields: { _id: { $toString: '$_id' } } }],
            },
          },
          { $addFields: { _id: { $toString: '$_id' }, confirmation: { $arrayElemAt: ['$confirmation', 0] }, deletedBy: { $arrayElemAt: ['$deletedBy', 0] } } },
        ]

        const items = await getItemsAggregate({ collection: 'event', pipeline })

        parseEventData(items, startFilter, endFilter)
      } catch (error) {
        console.log(error)
        handleError({ error, defaultMessage: i18n.t('message.err_confirmation_list') })
      } finally {
        isLoading.value = false
      }
    }

    function parseEventData(items, startFilter, endFilter) {
      const _globalCompliance = { Realizadas: 0, Vencidas: 0, Pendientes: 0 }
      const _locationCompliance = {}

      // TODO: change the supervisor from "organizer" to array of supervisors from attendee
      for (const element of items) {
        const status = element.extendedProps?.calendar
        const eventLocations = element.attendee ? element.attendee.locations : element.organizer?.locations

        // Global compliance
        if (status === 'Realizadas') _globalCompliance.Realizadas += 1
        else if (status === 'Pendientes' || status === 'Vencidas' || status === 'Futuras') _globalCompliance.Pendientes += 1
        // else if (status === "Vencidas") _globalCompliance["Vencidas"] += 1;

        // Location compliance
        if (eventLocations?.length) {
          for (const location of eventLocations) {
            if (locationFilter.value.length && !locationFilter.value.includes(location._id)) continue
            if (!_locationCompliance[location.location]) {
              _locationCompliance[location.location] = {
                done: 0,
                pending: 0,
                // overdue: 0,
                id: location._id,
              }
            }

            if (status === 'Realizadas') _locationCompliance[location.location].done += 1
            else if (status === 'Pendientes' || status === 'Vencidas' || status === 'Futuras') _locationCompliance[location.location].pending += 1
            // else if (status === "Vencidas") _locationCompliance[location.location]["overdue"] += 1;
          }
        }
      }

      // Set tracker data
      const complianceData = {
        total: _globalCompliance.Pendientes + _globalCompliance.Realizadas + _globalCompliance.Vencidas,
        done: _globalCompliance.Realizadas,
        pending: _globalCompliance.Pendientes,
        overdue: _globalCompliance.Vencidas,
      }

      // Set progress bar data
      const actualProgress = complianceData.total
        ? Math.round(complianceData.done / complianceData.total * 100)
        : 0

      const theoreticalProgress = endFilter < now
        ? 100
        : startFilter > now
          ? 0
          : Math.round(dateDiffInDays(startFilter, now) / dateDiffInDays(startFilter, endFilter) * 100)

      progressCompliance.value = {
        actual: actualProgress,
        theoretical: theoreticalProgress,
        totalDone: complianceData.done,
        pendingToMatchTheoretical: Math.max(0, Math.round(theoreticalProgress / 100 * complianceData.total) - complianceData.done),
      }

      // Set location compliance data
      for (const location in _locationCompliance) {
        _locationCompliance[location].total = _locationCompliance[location].done + _locationCompliance[location].pending
        _locationCompliance[location].compliance = _locationCompliance[location].total ? Math.round(_locationCompliance[location].done / _locationCompliance[location].total * 100) : 0
      }

      const locationComplianceDataSorted = Object.entries(_locationCompliance)

      locationComplianceDataSorted.sort((a, b) => {
        if (b[1].compliance === 0 && a[1].compliance === 0) return a[1].total - b[1].total
        return b[1].compliance - a[1].compliance
      })

      const locationComplianceLabels = []
      const locationComplianceValues = []
      const locationComplianceValuesDone = []
      const locationComplianceValuesPending = []
      // let locationComplianceValuesOverdue = []
      // let locationComplianceValuesPendingNegative = []
      // let locationComplianceValuesOverdueNegative = []
      const locationIdValues = []

      locationComplianceDataSorted.forEach(e => {
        locationComplianceLabels.push(e[0])
        locationComplianceValues.push(e[1].compliance)
        locationComplianceValuesDone.push(e[1].done)
        locationComplianceValuesPending.push(e[1].pending)
        // locationComplianceValuesOverdue.push(e[1].overdue)
        // locationComplianceValuesPendingNegative.push(-1 * e[1].pending)
        // locationComplianceValuesOverdueNegative.push(-1 * e[1].overdue)
        locationIdValues.push(e[1].id)
      })

      locationData.value = {
        location: locationComplianceLabels,
        compliance: locationComplianceValues,
        totalDone: locationComplianceValuesDone,
        totalPending: locationComplianceValuesPending,
        additionalInfo: {
          locationIds: locationIdValues,
        },
      }
    }

    const goToGlobalDashboard = () => {
      router.push({ name: 'habit-confirmation-dashboard' })
    }

    const filterByArea = areaId => {
      router.push({ name: 'habit-confirmation-dashboard', query: { areaId } })
    }

    // Cases

    const fetchCases = async () => {
      isLoadingCases.value = true

      const input = {
        client_id: clientId,
        workers: workerFilter.value,
        search: '',
        sortBy: 'createdAt',
        sortOrder: -1,
        limit: perPage.value,
        offset: (currentPage.value * perPage.value) - perPage.value,
        state: 'total',
        stage: null,
        onlyPendingTasks: false,
        workerId: userId,
        // Capa 2: mismo módulo que la lista de Case Management (widget "Casos" del
        // dashboard general usa la misma función de query) — ver permisos-plan.md.
        restrictToOwn: permissionScope('case-management', 'view') === 'own',
      }

      try {
        const items = await casesQueryWithOffset(input)

        caseStatesData.value = {
          labels: items?.casesStats?.stateCounts?.labels || [],
          values: items?.casesStats?.stateCounts?.values || [],
          total: items?.casesStats?.totalCount || 0,
        }
      } catch (error) {
        console.log(error)
        handleError({ error, defaultMessage: i18n.t('message.err_improvement_opportunities_list') })
      } finally {
        isLoadingCases.value = false
      }
    }

    // Complaints

    const fetchComplaints = async () => {
      isLoadingComplaints.value = true

      const input = {
        client_id: clientId,
        // workers: workerFilter.value,
        search: '',
        sortBy: 'createdAt',
        sortOrder: -1,
        limit: perPage.value,
        offset: (currentPage.value * perPage.value) - perPage.value,
        state: 'total',
        stage: null,
        onlyPendingTasks: false,
        workerId: userId,
        workerName: username,
        workerEmail: email,
        restrictToOwn: permissionScope('complaint-channel', 'view') === 'own',
      }

      try {
        const items = await complaintsQueryWithOffset(input)

        complaintStatesData.value = {
          labels: items?.complaintsStats?.stateCounts?.labels || [],
          values: items?.complaintsStats?.stateCounts?.values || [],
          total: items?.complaintsStats?.totalCount || 0,
        }
      } catch (error) {
        console.log(error)
        handleError({ error, defaultMessage: i18n.t('message.err_complaints_list') })
      } finally {
        isLoadingComplaints.value = false
      }
    }

    const filterByState = (state, origin, category) => {
      router.push({ name: `organization-${category}-list`, query: { state } })
    }

    // Process Adherence

    const listConfirmations = async () => {
      isLoadingConf.value = true
      const [startFilter, endFilter] = getDatesFromRange(dateRangeFilter.value)

      try {
        const initialQuery = {
          client_id: oid(clientId),
          date: { $gte: date(startFilter), $lt: date(endFilter) },
          pending: { $ne: true },
        }

        // Filter by risk if selected
        if (processFilter.value.length > 0) {
          initialQuery.process = { $in: processFilter.value.map(id => oid(id)) }
        }

        // Condiciones finales
        const conditions = []

        // Process type filter
        if (processTypeFilter.value.length) {
          conditions.push({
            $or: processTypeFilter.value.map(codePrefix => ({
              'process.code': { $regex: `^${codePrefix}` },
            })),
          })
        }

        // Privileges filter
        if (permissionScope('adherence-processes', 'view') === 'own') {
          if (userRole === 'employee') {
            conditions.push({
              $or: [
                { 'process.evaluator': userObjectId },
              ],
            })
          } else {
            conditions.push({
              $or: [
                { 'process.responsibles': userObjectId },
                { 'process.leadResponsibles': userObjectId },
                { 'process.evaluator': userObjectId },
              ],
            })
          }
        }

        if (companyFilter.value.length > 0) {
          conditions.push({ 'process.company': { $in: companyFilter.value.map(e => oid(e)) } })
        }
  
        if (idFilter.value.length > 0) {
          conditions.push({ 'process.id': { $in: idFilter.value.map(e => oid(e)) } })
        }

        // Armar el match final
        const finalQuery = conditions.length
          ? { $and: conditions }
          : {}

        const pipeline = [
          { $match: initialQuery },
          {
            $lookup: {
              from: 'process',
              localField: 'process',
              foreignField: '_id',
              pipeline: [{
                $project: {
                  name: 1, code: 1, responsibles: 1, leadResponsibles: 1, evaluator: 1, company: 1, id: 1,
                },
              }, { $addFields: { _id: { $toString: '$_id' } } }],
              as: 'process',
            },
          },
          { $addFields: { process: { $arrayElemAt: ['$process', 0] } } },
          { $match: finalQuery },
          {
            $lookup: {
              from: 'worker',
              localField: 'worker',
              foreignField: '_id',
              pipeline: [
                { $project: { name: 1, locations: 1, roles: 1 } },
                {
                  $lookup: {
                    from: 'location',
                    localField: 'locations',
                    foreignField: '_id',
                    pipeline: [{ $project: { location: 1, zone: 1, agency: 1 } }, { $addFields: { _id: { $toString: '$_id' } } }],
                    as: 'locations',
                  },
                },
                { $addFields: { _id: { $toString: '$_id' } } },
              ],
              as: 'worker',
            },
          },
          {
            $lookup: {
              from: 'worker',
              localField: 'supervisor',
              foreignField: '_id',
              pipeline: [
                { $project: { name: 1, locations: 1 } },
                {
                  $lookup: {
                    from: 'location',
                    localField: 'locations',
                    foreignField: '_id',
                    pipeline: [{ $project: { location: 1, zone: 1, agency: 1 } }, { $addFields: { _id: { $toString: '$_id' } } }],
                    as: 'locations',
                  },
                },
                { $addFields: { _id: { $toString: '$_id' } } },
              ],
              as: 'supervisor',
            },
          },
          { $addFields: { _id: { $toString: '$_id' }, worker: { $arrayElemAt: ['$worker', 0] }, supervisor: { $arrayElemAt: ['$supervisor', 0] } } },
        ]

        const items = await getItemsAggregate({ collection: 'confirmation', pipeline })

        parseConfirmationData(items, startFilter)
      } catch (error) {
        console.log(error)
        handleError({ error, defaultMessage: i18n.t('message.err_confirmation_list') })
      } finally {
        isLoadingConf.value = false
      }
    }

    const parseConfirmationData = (items, startFilter) => {
      let confirmationCount = 0
      const adherenceData = { score: 0, total: 0 }
      const processData = {}

      for (const confirmation of items) {
        if (confirmation.total) {
          const processName = confirmation.process?.code
            ? `${confirmation.process.code} / ${confirmation.process?.name}`
            : confirmation.process?.name
          const processId = confirmation.process?._id
          const { score } = confirmation
          const { total } = confirmation

          confirmationCount++
          adherenceData.score += score
          adherenceData.total += total

          // Data for bar charts
          if (processName) updateObjectData(processData, processName, processId, score, total)
        }
      }
      confirmations_total.value = confirmationCount
      adherence_index.value = adherenceData.total === 0
        ? '-'
        : `${processAdherence(adherenceData.score, adherenceData.total)}%`

      // Set charts data
      adherenceByProcess.value = setChartsData(processData, 'desc')
    }

    const updateObjectData = (object, property, propertyId, score, total) => {
      if (object.hasOwnProperty(property)) {
        object[property].score += score
        object[property].total += total
        object[property].confirmations += 1
      } else {
        object[property] = {
          score,
          total,
          confirmations: 1,
          id: propertyId,
        }
      }
    }

    const setChartsData = (objectData, sortDirection, dataLimit) => {
      for (const property in objectData) {
        objectData[property].adherence = processAdherence(objectData[property].score, objectData[property].total)
      }

      const dataSorted = Object.entries(objectData)

      dataSorted.sort((a, b) => {
        if (b[1].score === 0 && a[1].score === 0) return sortDirection === 'desc' ? a[1].total - b[1].total : b[1].total - a[1].total
        return sortDirection === 'desc' ? b[1].adherence - a[1].adherence : a[1].adherence - b[1].adherence
      })

      const limit = dataLimit < dataSorted.length ? dataLimit : dataSorted.length

      const labels = []
      const adherenceValues = []
      const confirmationValues = []
      const idValues = []
      const completeLabels = []

      for (let i = 0; i < limit; i++) {
        labels.push(dataSorted[i][0].length > 24 ? `${dataSorted[i][0].slice(0, 24)}...` : dataSorted[i][0])
        adherenceValues.push(dataSorted[i][1].adherence)
        confirmationValues.push(dataSorted[i][1].confirmations)
        idValues.push(dataSorted[i][1].id)
        completeLabels.push(dataSorted[i][0])
      }

      return {
        labels,
        data: {
          adherence: adherenceValues,
          confirmations: confirmationValues,
        },
        additionalInfo: {
          ids: idValues,
          completeLabels,
        },
      }
    }

    const goToAdherenceDashboard = () => {
      router.push({ name: 'habit-adherence-dashboard' })
    }

    const filterByProcess = processId => {
      router.push({ name: 'habit-adherence-dashboard', query: { processId } })
    }

    // Commitments

    const listCommitments = async () => {
      isLoadingComm.value = true
      const [startFilter, endFilter] = getDatesFromRange(dateRangeFilter.value)

      const input = {
        clientId,
        userId,
        restrictToOwn: permissionScope('adherence-improvement', 'view') === 'own',
        locations: locationFilter.value,
        roles: roleFilter.value,
        processes: processFilter.value,
        workers: workerFilter.value,
        date_gte: startFilter,
        date_lt: endFilter,
        processTypeFilter: processTypeFilter.value,
        companyFilter: companyFilter.value,
        idFilter: idFilter.value,
      }

      try {
        const items = await commitmentReport(input)
        const workersWithCommitments = items?.commitments || []

        parseCommitmentData(workersWithCommitments)
      } catch (error) {
        console.log(error)
        handleError({ error, defaultMessage: i18n.t('message.err_worker_list') })
      } finally {
        isLoadingComm.value = false
      }
    }

    const parseCommitmentData = workersWithCommitments => {
      const labels = []
      const processAdherenceValues = []
      const increasedAdherenceValues = []
      const unresolvedGapValues = []
      const gapWithoutCommitmentValues = []
      const workerIds = []

      let totalScoreCount = 0
      let totalTotalCount = 0
      let adjustedScoreCount = 0
      let unresolvedGapCount = 0
      let commitmentsFinishedCount = 0
      let commitmentsGeneratedCount = 0
      let commitmentsNotGeneratedCount = 0

      for (const worker of workersWithCommitments) {
        const behavioursFailed = []

        for (const e of worker.confirmationsData) {
          if (worker.averageAdherence === 1) continue

          e.activities?.forEach(a => {
            a.behaviours?.forEach(b => {
              if ((b.type === 'bool' && (b.answer === 'true' && !b.expectedAnswer) || (b.answer === 'false' && b.expectedAnswer))
                || (b.type === 'rating' && (b.answer === '1' || b.answer === '2' || b.answer === '3' || b.answer === '4'))
              ) {
                behavioursFailed.push({
                  activity: a.name,
                  behaviour: b.name,
                  type: b.type,
                  answer: b.answer,
                  potentialImprovementScore: b.type === 'rating' ? (5 - parseInt(b.answer)) / 5 : 1,
                })
              }
            })
          })

          e.cases?.forEach(c => {
            for (const behaviourFailed of behavioursFailed) {
              if (behaviourFailed.activity === c.activity && behaviourFailed.behaviour === c.behaviour?.name) {
                if (c.stage === 'end') {
                  behaviourFailed.status = 'success'
                } else {
                  behaviourFailed.status = 'pending'
                }
                break
              }
            }
          })
        }

        const commitmentsCompleted = behavioursFailed.filter(e => e.status === 'success')
        const commitmentsPending = behavioursFailed.filter(e => e.status === 'pending')
        const increasedAdherence = commitmentsCompleted.reduce((sum, item) => sum + item.potentialImprovementScore, 0)
        const unresolvedGap = commitmentsPending.reduce((sum, item) => sum + item.potentialImprovementScore, 0)
        const gapWithoutCommitment = worker.totalTotal - worker.totalScore - increasedAdherence - unresolvedGap

        labels.push(worker.name)
        processAdherenceValues.push(processAdherence(worker.totalScore, worker.totalTotal))
        increasedAdherenceValues.push(processAdherence(increasedAdherence, worker.totalTotal))
        unresolvedGapValues.push(processAdherence(unresolvedGap, worker.totalTotal))
        gapWithoutCommitmentValues.push(processAdherence(gapWithoutCommitment, worker.totalTotal))
        workerIds.push(worker._id)

        totalScoreCount += worker.totalScore
        totalTotalCount += worker.totalTotal
        adjustedScoreCount += increasedAdherence
        unresolvedGapCount += unresolvedGap
        commitmentsFinishedCount += commitmentsCompleted.length
        commitmentsGeneratedCount += commitmentsCompleted.length + commitmentsPending.length
        commitmentsNotGeneratedCount += behavioursFailed.length - commitmentsCompleted.length - commitmentsPending.length
      }

      adherenceTotal.value = `${processAdherence(totalScoreCount, totalTotalCount)}%`
      adherenceAdjusted.value = `${processAdherence(totalScoreCount + adjustedScoreCount, totalTotalCount)}%`
      commitmentsGenerated.value = commitmentsGeneratedCount
      commitmentCompliance.value = `${processAdherence(commitmentsFinishedCount, commitmentsGeneratedCount)}%`
      commitmentsNotGenerated.value = commitmentsNotGeneratedCount

      commitmentsData.value = {
        labels: labels.slice(0, 10),
        data: {
          processAdherenceValues: processAdherenceValues.slice(0, 10),
          increasedAdherenceValues: increasedAdherenceValues.slice(0, 10),
          unresolvedGapValues: unresolvedGapValues.slice(0, 10),
          gapWithoutCommitmentValues: gapWithoutCommitmentValues.slice(0, 10),
        },
        additionalInfo: {
          workerIds,
        },
      }
    }

    async function listRisks() {
      isLoadingRisks.value = true

      residualRiskData.value = []
      inherentRiskData.value = []
      quadrantCounters.value = {}

      try {
        const query = {
          client_id: oid(clientId),
          deleted: { $ne: true },
        }

        if (processFilter.value.length > 0) {
          query._id = { $in: processFilter.value.map(id => oid(id)) }
        }

        if (companyFilter.value.length > 0) {
          query.company = { $in: companyFilter.value.map(e => oid(e)) }
        }
  
        if (idFilter.value.length > 0) {
          query.id = { $in: idFilter.value.map(e => oid(e)) }
        }

        const conditions = []

        // Process type filter
        if (processTypeFilter.value.length) {
          conditions.push({
            $or: processTypeFilter.value.map(codePrefix => ({
              code: { $regex: `^${codePrefix}` },
            })),
          })
        }

        // Privileges filter
        if (permissionScope('adherence-processes', 'view') === 'own') {
          if (userRole === 'employee') {
            conditions.push({
              $or: [
                { evaluator: userObjectId },
              ],
            })
          } else {
            conditions.push({
              $or: [
                { responsibles: userObjectId },
                { leadResponsibles: userObjectId },
                { evaluator: userObjectId },
              ],
            })
          }
        }

        // Armar el match final
        if (conditions.length) {
          query.$and = conditions
        }

        const pipeline = [{ $match: query }]

        const items = await getItemsAggregate({ collection: 'process', pipeline })

        parseRiskData(items)
      } catch (error) {
        console.log(error)
        handleError({ error, defaultMessage: i18n.t('message.process_list_fetch_error') })
      } finally {
        isLoadingRisks.value = false
      }
    }

    // Función para convertir valores categóricos a continuos con padding. Esto ayuda a distribuir mejor los puntos en el gráfico de burbujas.
    function toContinuous(value, padding = 0.2) {
      return value + padding + Math.random() * (1 - 2 * padding)
    }

    // Lógica para distribuir riesgos uniformemente dentro de cada cuadrante
    const ROWS = 3
    const COLS = 5
    const CELL_FILL = 0.6
    const VERTICAL_OFFSET = 0.08 // 👈 mueve todo hacia abajo

    function getDistributedPosition(baseX, baseY, indexInQuadrant) {
      // Si hay más puntos que celdas, devolver una posición aleatoria dentro del cuadrante
      if (indexInQuadrant >= ROWS * COLS) {
        return {
          x: toContinuous(baseX),
          y: toContinuous(baseY),
        }
      }

      const row = Math.floor(indexInQuadrant / COLS)
      const col = indexInQuadrant % COLS

      const cellWidth = 1 / COLS
      const cellHeight = 1 / ROWS

      const offsetX = (cellWidth * (1 - CELL_FILL)) / 2
      const offsetY = (cellHeight * (1 - CELL_FILL)) / 2

      const x = baseX + col * cellWidth + offsetX + (cellWidth * CELL_FILL) / 2
      let y = baseY + (ROWS - row - 1) * cellHeight + offsetY + (cellHeight * CELL_FILL) / 2
      y -= VERTICAL_OFFSET // empujar todo el cuadrante hacia abajo

      return { x, y }
    }

    function addRiskToChartData(probability, impact, name, code, isResidual) {
      const probabilityValue = probabilityValues[probability]
      const impactValue = impactValues[impact]

      const quadrantKey = `${impactValue}-${probabilityValue}`

      if (!quadrantCounters.value[quadrantKey]) {
        quadrantCounters.value[quadrantKey] = 0
      }

      const indexInQuadrant = quadrantCounters.value[quadrantKey]
      const { x: probabilityValueAdjusted, y: impactValueAdjusted } = getDistributedPosition(probabilityValue, impactValue, indexInQuadrant)

      quadrantCounters.value[quadrantKey]++

      if (isResidual) {
        residualRiskData.value.push({
          x: probabilityValueAdjusted,
          y: impactValueAdjusted,
          name: name || '',
          code: code || '',
          probability: probability || '',
          impact: impact || '',
        })
      } else {
        inherentRiskData.value.push({
          x: probabilityValueAdjusted,
          y: impactValueAdjusted,
          name: name || '',
          code: code || '',
          probability: probability || '',
          impact: impact || '',
        })
      }
    }

    function parseRiskData(items) {
      for (const risk of items) {
        // Residual risk
        if (risk.residualRiskProbability && risk.residualRiskImpact) {
          addRiskToChartData(risk.residualRiskProbability, risk.residualRiskImpact, risk.name, risk.code, true)
        }

        // Inherent risk
        if (risk.inherentRiskProbability && risk.inherentRiskImpact) {
          addRiskToChartData(risk.inherentRiskProbability, risk.inherentRiskImpact, risk.name, risk.code, false)
        }
      }
    }

    const goToCommitmentDashboard = () => {
      router.push({ name: 'habit-commitment-dashboard' })
    }

    const filterByWorker = workerId => {
      router.push({ name: 'habit-commitment-dashboard', query: { workerId } })
    }

    // Risk Prediction
    const fetchRiskEvaluations = async () => {
      isLoadingRiskPrediction.value = true

      try {
        const initialQuery = {
          client_id: oid(clientId),
          pending: { $ne: true },
          deleted: { $ne: true },
        }

        // Filter by risk if selected
        if (processFilter.value.length > 0) {
          initialQuery.process = { $in: processFilter.value.map(id => oid(id)) }
        }

        const conditions = []

        // Process type filter
        if (processTypeFilter.value.length) {
          conditions.push({
            $or: processTypeFilter.value.map(codePrefix => ({
              'process.code': { $regex: `^${codePrefix}` },
            })),
          })
        }

        // Privileges filter
        if (['manager', 'supervisor'].includes(userRole)) {
          conditions.push({
            $or: [
              { 'process.responsibles': userObjectId },
              { 'process.leadResponsibles': userObjectId },
              { 'process.evaluator': userObjectId },
            ],
          })
        } else if (userRole === 'employee') {
          conditions.push({
            $or: [
              { 'process.evaluator': userObjectId },
            ],
          })
        }

        if (companyFilter.value.length > 0) {
          conditions.push({ 'process.company': { $in: companyFilter.value.map(e => oid(e)) } })
        }
  
        if (idFilter.value.length > 0) {
          conditions.push({ 'process.id': { $in: idFilter.value.map(e => oid(e)) } })
        }

        const finalQuery = conditions.length
          ? { $and: conditions }
          : {}

        const pipeline = [
          { $match: initialQuery },
          { $sort: { date: 1 } },
          {
            $lookup: {
              from: 'process',
              localField: 'process',
              foreignField: '_id',
              pipeline: [{
                $project: {
                  name: 1, code: 1, responsibles: 1, leadResponsibles: 1, evaluator: 1, company: 1, id: 1,
                },
              }, { $addFields: { _id: { $toString: '$_id' } } }],
              as: 'process',
            },
          },
          { $addFields: { process: { $arrayElemAt: ['$process', 0] }, _id: { $toString: '$_id' } } },
          { $match: finalQuery },
        ]

        const items = await getItemsAggregate({ collection: 'confirmation', pipeline })

        calculateRiskPrediction(items)
      } catch (error) {
        console.log(error)
        handleError({ error, defaultMessage: i18n.t('message.err_confirmation_list') })
      } finally {
        isLoadingRiskPrediction.value = false
      }
    }

    const calculateRiskPrediction = evaluations => {
      if (!evaluations || evaluations.length === 0) {
        riskPredictionValue.value = 0
        riskTrendDirection.value = null
        riskTrendPercentage.value = 0
        return
      }

      // Calculate adherence percentages for each evaluation
      const adherencePercentages = evaluations
        .filter(e => e.total && e.total > 0)
        .map(e => (e.score / e.total) * 100)

      if (adherencePercentages.length === 0) {
        riskPredictionValue.value = 0
        riskTrendDirection.value = null
        riskTrendPercentage.value = 0
        return
      }

      let baseEvaluations = []
      let recentEvaluations = []

      if (processFilter.value.length > 0) {
        // With risk filter: Base = 45% of total evaluations (minimum 1)
        const baseCount = Math.max(1, Math.round(adherencePercentages.length * 0.45))
        baseEvaluations = adherencePercentages.slice(0, baseCount)
        recentEvaluations = adherencePercentages.slice(-10)
      } else {
        // No filter (global): Initial base = first 30, update every 10 new evaluations
        if (adherencePercentages.length <= 30) {
          baseEvaluations = adherencePercentages
          recentEvaluations = adherencePercentages.slice(-Math.min(10, adherencePercentages.length))
        } else {
          const cycles = Math.floor((adherencePercentages.length - 30) / 10)
          const baseCount = 30 + (cycles * 10)
          baseEvaluations = adherencePercentages.slice(0, baseCount)
          recentEvaluations = adherencePercentages.slice(-10)
        }
      }

      // Calculate accumulated average (base)
      const accumulatedAverage = baseEvaluations.reduce((sum, val) => sum + val, 0) / baseEvaluations.length

      // Calculate recent average (last 10)
      const recentAverage = recentEvaluations.length > 0
        ? recentEvaluations.reduce((sum, val) => sum + val, 0) / recentEvaluations.length
        : accumulatedAverage

      // Convert adherence to RISK (invert: high adherence = low risk, low adherence = high risk)
      const riskPercentage = 100 - accumulatedAverage
      riskPredictionValue.value = Math.max(0, Math.min(100, riskPercentage))

      // Calculate trend
      const trendDiff = recentAverage - accumulatedAverage
      const trendPercentageValue = Math.abs(trendDiff)

      if (trendPercentageValue < 1) {
        riskTrendDirection.value = 'stable'
        riskTrendPercentage.value = 0
      } else if (trendDiff > 0) {
        // Adherence increasing = Risk decreasing
        riskTrendDirection.value = 'down'
        riskTrendPercentage.value = Math.round(trendPercentageValue * 10) / 10
      } else {
        // Adherence decreasing = Risk increasing
        riskTrendDirection.value = 'up'
        riskTrendPercentage.value = Math.round(trendPercentageValue * 10) / 10
      }
    }

    const updateFilter = data => {
      dateRangeFilter.value = data.dateRangeFilter
      processTypeFilter.value = data.processTypeFilter
      // processFilter ya se actualiza automáticamente desde el store global

      listEvents()
      listConfirmations()
      listCommitments()
      listRisks()
      fetchRiskEvaluations()
    }

    onMounted(() => {
      listEvents()
      fetchCases()
      fetchComplaints()
      listConfirmations()
      listCommitments()
      listRisks()
      fetchRiskEvaluations()
    })

    watch([companyFilter, idFilter], () => {
      listEvents()
      listConfirmations()
      listCommitments()
      listRisks()
      fetchRiskEvaluations()
    })

    return {
      progressCompliance,
      updateFilter,
      isLoading,
      isLoadingCases,
      isLoadingComplaints,
      isLoadingConf,
      isLoadingComm,
      isLoadingRisks,
      isLoadingRiskPrediction,
      locationData,
      router,
      confirmations_total,
      adherence_index,
      adherenceByProcess,
      adherenceTotal,
      adherenceAdjusted,
      commitmentsGenerated,
      commitmentCompliance,
      commitmentsNotGenerated,
      commitmentsData,
      caseStatesData,
      complaintStatesData,
      residualRiskData,
      inherentRiskData,
      riskPredictionValue,
      riskTrendDirection,
      riskTrendPercentage,
      goToGlobalDashboard,
      filterByArea,
      filterByState,
      filterByProcess,
      filterByWorker,
      goToAdherenceDashboard,
      goToCommitmentDashboard,
    }
  },
}
</script>
